<template>
  <Modal contentWidth="1200px" :required=true @close="$emit('close')">
    <template #header>
      <h5 class="mb-0" style="line-height: 0.675rem; color: #163D7D;">Solicitar Ticket</h5>
    </template>
    <template #body>
      <b-overlay :show="processing" rounded="sm">
        <template #overlay>
          <div class="d-flex flex-column justify-content-center align-items-center w-100 fs-19 text-info">
            <p>Creando ticket, por favor espere</p>
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <!-- Uploader overlay -->
        <b-overlay :show="showUploader" blur="3px" opacity="0.6" variant="white" rounded="lg">
          <template #overlay>
            <div class="bg-white shadow-lg rounded-lg p-3" style="width: 560px; max-width: 90vw;">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="font-weight-bold text-dark mb-0">
                  Adjuntar archivos
                  <small class="text-muted font-weight-normal ml-1">{{ attachments.length }}/{{ maxAttachments }}</small>
                </h6>
                <button type="button" class="close" aria-label="Cerrar" @click="showUploader = false">&times;</button>
              </div>
              <AttachmentsUploader
                :allowedFileTypes="allowedFileTypes"
                :maxNumberOfFiles="maxAttachments - attachments.length"
                :maxFileSize="10485760"
                :useDashboard="true"
                :dashboardHeight="260"
                @file-added="handleFileAdded"
                @file-removed="handleFileRemoved"
                @handle-done-button="showUploader = false"
              />
            </div>
          </template>

          <ValidationObserver>
            <b-row class="d-block d-sm-none">
              <b-col>
                <p v-if="!processing && createdSuccess" class="text-success mb-1 mt-2 fs-14">
                  <font-awesome-icon :icon="['far', 'check-circle']" fixed-width />
                  Ticket creado correctamente
                </p>
                <p v-if="!processing && createdFailure" class="text-danger mb-1 mt-2 fs-14">
                  <font-awesome-icon :icon="['far', 'times-circle']" fixed-width />
                  <span>{{ errorMsg }}</span>
                </p>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <div class="text-left">
                  <LevelSelector
                    :multiColumn="true"
                    @setInternalTicketLevel="setInternalTicketLevel"
                  >
                    <template #label>
                      <div class="d-flex justify-content-between">
                        <p class="mb-1 text-muted">Seleccione el tipo de solicitud <span class="text-danger ml-1">*</span></p>
                        <div class="d-none d-sm-block">
                          <p v-if="!processing && createdSuccess" class="text-success mb-1 mt-2">
                            <font-awesome-icon :icon="['far', 'check-circle']" fixed-width />
                            Ticket creado correctamente
                          </p>
                          <p v-if="!processing && createdFailure" class="text-danger mb-1 mt-2">
                            <font-awesome-icon :icon="['far', 'times-circle']" fixed-width />
                            <span>{{ errorMsg }}</span>
                          </p>
                        </div>
                      </div>
                    </template>
                  </LevelSelector>
                </div>

                <div class="text-left mt-1">
                  <p class="mb-1 text-muted">Asunto <span class="text-danger ml-1">*</span></p>
                  <ValidationProvider rules="required" name="Asunto" v-slot="{ valid, errors }">
                    <b-form-input
                      v-model="form.title"
                      type="text"
                      :state="errors[0] || !form.title ? false : (valid ? true : null)"
                    />
                  </ValidationProvider>
                </div>

                <div class="text-left mt-1">
                  <p class="mb-1 text-muted">Descripción <span class="text-danger ml-1">*</span></p>
                  <ValidationProvider rules="required" name="Descripción" v-slot="{ valid, errors }">
                    <b-form-textarea
                      v-model="form.description"
                      type="text"
                      placeholder="Describa su solicitud de forma detallada"
                      :state="errors[0] || !form.description ? false : (valid ? true : null)"
                      rows="5"
                    />
                  </ValidationProvider>
                </div>

                <!-- Attachments -->
                <div class="text-left mt-3">
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <p class="mb-0 text-muted">Adjuntos <small>(opcional)</small></p>
                      <small class="text-muted">Hasta {{ maxAttachments }} archivos · máx. 10 MB cada uno</small>
                    </div>
                    <b-button
                      variant="outline-primary"
                      :disabled="attachments.length >= maxAttachments"
                      @click="showUploader = true"
                    >
                      <font-awesome-icon :icon="['far', 'paperclip']" fixed-width />
                      <span class="ml-1">Adjuntar archivo</span>
                      <small class="ml-1">{{ attachments.length }}/{{ maxAttachments }}</small>
                    </b-button>
                  </div>

                  <div v-if="attachments.length" class="d-flex flex-wrap bg-light rounded-lg p-2 mt-2" style="gap: 0.5rem;">
                    <div
                      v-for="(attachment, idx) in attachments"
                      :key="attachment.file_id"
                      class="d-flex align-items-center border rounded-pill bg-white pl-3 pr-1"
                      style="max-width: 260px; height: 36px;"
                    >
                      <font-awesome-icon
                        :icon="['far', attachment.type == 'image' ? 'image' : 'file-alt']"
                        :class="attachment.type == 'image' ? 'text-primary' : 'text-danger'"
                        fixed-width
                      />
                      <span class="ml-1 text-dark text-truncate" style="font-size: 0.85rem;" :title="attachment.name">{{ attachment.name }}</span>
                      <button
                        type="button"
                        class="close ml-2 mr-1"
                        style="font-size: 1.2rem;"
                        :aria-label="`Quitar ${attachment.name}`"
                        @click="removeAttachment(idx)"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </ValidationObserver>
        </b-overlay>
      </b-overlay>
    </template>
    <template #footer>
      <b-button variant="outline-danger" @click="$emit('close')">
        Cancelar
      </b-button>
      <b-button :disabled="invalidTicket" variant="outline-success" @click="createTicket">
        Crear ticket
      </b-button>
    </template>
  </Modal>
</template>

<script>
import Modal from '../../shared/Modal'
import LevelSelector from './LevelSelector'
import { ValidationObserver, ValidationProvider } from "vee-validate"
import AttachmentsUploader from '../../shared/AttachmentsUploader'

import { allowedFileTypes } from '../../../sharedOptions/ticketsModule'

export default {
  name: 'NewTicketModal',
  components: { Modal, ValidationObserver, ValidationProvider, LevelSelector, AttachmentsUploader },
  props: {
  },
  data () {
    return {
      allowedFileTypes: allowedFileTypes,
      maxAttachments: 5,
      processing: false,
      createdSuccess: false,
      createdFailure: false,
      errorMsg: '',
      form: {
        title: null,
        description: null,
        internal_ticket_level_id: null
      },
      attachments: [],
      showUploader: false
    }
  },
  computed: {
    invalidTicket () {
      if (this.processing || this.showUploader) return true
      if (!this.form.title || !this.form.description || !this.form.internal_ticket_level_id) {
        return true
      }
      return false
    }
  },
  methods: {
    setInternalTicketLevel (level_id) {
      this.form.internal_ticket_level_id = level_id
    },
    createTicket () {
      if (this.processing) return
      this.processing = true
      this.createdSuccess = false
      this.createdFailure = false

      const body = {
        internal_ticket: { ...this.form }
      }

      if (this.attachments.length) {
        body.attachments = this.attachments.map(attachment => (
          attachment.type == 'image'
            ? { image: attachment.image, caption: attachment.caption }
            : { file: attachment.file, name: attachment.name }
        ))
      }

      this.$http.post('/api/production/internal_tickets', body)
        .then(response => {
          this.form.title = null
          this.form.description = null
          this.attachments = []
          this.createdSuccess = true
          this.$emit('on-create-ticket', response.data)
        })
        .catch(error => {
          console.log(error.response.data.error)
          if (error.response?.data?.error && error.response.data.error == 'La validación falló: Documents no es válido') {
            this.errorMsg = 'El documento que está intentando subir no es válido'
          } else {
            this.errorMsg = error.response?.data?.error || 'Hubo un error al crear el ticket, intente nuevamente'
          }
          this.createdFailure = true
        })
        .finally(() => {
          this.processing = false
          setTimeout(() => {
            this.createdSuccess = false
            this.createdFailure = false
          }, 7000)
        })
    },
    handleFileAdded (form) {
      if (this.attachments.length >= this.maxAttachments) return
      this.attachments.push(form)
    },
    handleFileRemoved (file) {
      this.attachments = this.attachments.filter(attachment => attachment.file_id !== file.file_id)
    },
    removeAttachment (idx) {
      this.attachments.splice(idx, 1)
    }
  }
}
</script>

<style scoped lang="scss">
  textarea {
    font-size: 17px;
    &::placeholder {
      font-size: 17px;
    }
  }
</style>
